import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";

import { PAYMENT_METHODS, PAYMENT_METHOD_LABELS, can, type PaymentMethod } from "@devengo/shared";

import {
  Badge,
  Button,
  Card,
  ErrorBox,
  Field,
  Input,
  PageTitle,
  Select,
  numClass,
  tableClass,
  tdClass,
  thClass,
  NoAccess,
} from "../components/ui";
import { api } from "../lib/api";
import { useCurrentUser } from "../lib/session";
import { formatCuit, formatDate, formatDecimal, parseUserAmount } from "../lib/format";
import { MonthlyFeePanel } from "./monthly-fee-panel";

interface Movement {
  kind: "charge" | "payment";
  id: string;
  date: string;
  amount: string;
  runningBalance: string;
  voided: boolean;
  voidReason: string | null;
  source?: "manual" | "monthly_fee";
  conceptName?: string;
  description?: string | null;
  dueDate?: string | null;
  method?: PaymentMethod;
  note?: string | null;
}

export interface Account {
  companyId: string;
  legalName: string;
  cuit: string;
  balance: string;
  owed: string;
  overdue: string;
  favour: string;
  oldestUnpaidDate: string | null;
  openingBalance: string;
  closingBalance: string;
  movements: Movement[];
}

interface Concept {
  id: string | null;
  name: string;
  archived: boolean;
}

const today = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: "America/Argentina/Buenos_Aires" }).format(
    new Date(),
  );

/** The current account of one client company with the studio (spec 015 Story 3). Dueño only. */
export function ReceivableAccountPage({ companyId }: { companyId: string }) {
  const user = useCurrentUser();
  if (!can(user.role, "manageReceivables")) return <NoAccess />;
  return <ReceivableAccount companyId={companyId} />;
}

function ReceivableAccount({ companyId }: { companyId: string }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [form, setForm] = useState<"charge" | "payment" | null>(null);
  const qs = new URLSearchParams();
  if (from) qs.set("from", from);
  if (to) qs.set("to", to);
  const account = useQuery({
    queryKey: ["receivables", "account", companyId, from, to],
    queryFn: () =>
      api.get<Account>(
        `/api/receivables/companies/${companyId}/account${qs.size ? `?${qs.toString()}` : ""}`,
      ),
  });
  const data = account.data;
  const owes = data ? Number(data.balance) : 0;

  return (
    <>
      <PageTitle
        subtitle={
          data ? (
            <>
              CUIT {formatCuit(data.cuit)} ·{" "}
              <Link to="/cobranzas" className="text-brand-primary underline">
                volver a Cobranzas
              </Link>
            </>
          ) : null
        }
        actions={
          <>
            <Button onClick={() => setForm(form === "charge" ? null : "charge")}>
              Registrar cargo
            </Button>
            <Button onClick={() => setForm(form === "payment" ? null : "payment")}>
              Registrar cobro
            </Button>
          </>
        }
      >
        Cuenta corriente {data ? `· ${data.legalName}` : ""}
      </PageTitle>
      <ErrorBox error={account.error} />

      {data ? (
        <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
          <Stat
            label={owes < 0 ? "Saldo a favor del cliente" : "Debe hoy"}
            value={formatDecimal(data.balance.replace(/^-/, ""))}
            tone={owes > 0 ? "warning" : "neutral"}
          />
          <Stat
            label="Vencido"
            value={formatDecimal(data.overdue)}
            tone={Number(data.overdue) > 0 ? "danger" : "neutral"}
          />
          <Stat
            label="Cargo impago más viejo"
            value={data.oldestUnpaidDate ? formatDate(data.oldestUnpaidDate) : "—"}
          />
          <Stat label="Movimientos" value={String(data.movements.length)} />
        </div>
      ) : null}

      {form === "charge" ? <ChargeForm companyId={companyId} onDone={() => setForm(null)} /> : null}
      {form === "payment" ? (
        <PaymentForm companyId={companyId} onDone={() => setForm(null)} />
      ) : null}

      <MonthlyFeePanel companyId={companyId} />

      <Card
        title="Movimientos"
        actions={
          <div className="flex items-center gap-2 text-sm">
            <span className="text-brand-muted">Desde</span>
            <Input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="w-40"
            />
            <span className="text-brand-muted">hasta</span>
            <Input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="w-40"
            />
          </div>
        }
      >
        {data ? (
          <table className={tableClass}>
            <thead>
              <tr>
                <th className={thClass}>Fecha</th>
                <th className={thClass}>Detalle</th>
                <th className={thClass}>Vence</th>
                <th className={`${thClass} text-right`}>Cargo</th>
                <th className={`${thClass} text-right`}>Cobro</th>
                <th className={`${thClass} text-right`}>Saldo</th>
                <th className={thClass} />
              </tr>
            </thead>
            <tbody>
              {from ? (
                <tr>
                  <td className={tdClass} colSpan={5}>
                    Saldo anterior al {formatDate(from)}
                  </td>
                  <td className={`${tdClass} ${numClass} font-medium`}>
                    {formatDecimal(data.openingBalance)}
                  </td>
                  <td className={tdClass} />
                </tr>
              ) : null}
              {data.movements.map((m) => (
                <MovementRow key={m.id} movement={m} />
              ))}
              {data.movements.length === 0 ? (
                <tr>
                  <td className={`${tdClass} text-brand-muted`} colSpan={7}>
                    No hay movimientos{from || to ? " en esas fechas" : " todavía"}.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        ) : null}
      </Card>
    </>
  );
}

function Stat({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string;
  tone?: "neutral" | "warning" | "danger";
}) {
  const color =
    tone === "danger" ? "text-red-700" : tone === "warning" ? "text-amber-800" : "text-brand-text";
  return (
    <div className="rounded-lg border border-brand-line bg-white px-4 py-3 shadow-sm">
      <p className="text-xs uppercase tracking-wide text-brand-muted">{label}</p>
      <p className={`text-xl font-semibold tabular ${color}`}>{value}</p>
    </div>
  );
}

function MovementRow({ movement: m }: { movement: Movement }) {
  const [voiding, setVoiding] = useState(false);
  const [reason, setReason] = useState("");
  const client = useQueryClient();
  const voidIt = useMutation({
    mutationFn: () =>
      api.post(`/api/receivables/${m.kind === "charge" ? "charges" : "payments"}/${m.id}/void`, {
        reason,
      }),
    onSuccess: async () => {
      setVoiding(false);
      await client.invalidateQueries({ queryKey: ["receivables"] });
    },
  });
  const struck = m.voided ? "line-through text-brand-muted" : "";
  const detail =
    m.kind === "charge"
      ? `${m.conceptName ?? ""}${m.description ? ` · ${m.description}` : ""}`
      : `Cobro · ${m.method ? PAYMENT_METHOD_LABELS[m.method] : ""}${m.note ? ` · ${m.note}` : ""}`;
  return (
    <>
      <tr>
        <td className={`${tdClass} ${struck} tabular`}>{formatDate(m.date)}</td>
        <td className={tdClass}>
          <span className={struck}>{detail}</span>{" "}
          {m.source === "monthly_fee" ? <Badge tone="brand">Abono</Badge> : null}{" "}
          {m.voided ? (
            <span className="block text-xs text-red-700">
              <Badge tone="danger">Anulado</Badge> {m.voidReason}
            </span>
          ) : null}
        </td>
        <td className={`${tdClass} tabular`}>{m.dueDate ? formatDate(m.dueDate) : ""}</td>
        <td className={`${tdClass} ${numClass} ${struck}`}>
          {m.kind === "charge" ? formatDecimal(m.amount) : ""}
        </td>
        <td className={`${tdClass} ${numClass} ${struck}`}>
          {m.kind === "payment" ? formatDecimal(m.amount) : ""}
        </td>
        <td className={`${tdClass} ${numClass}`}>{formatDecimal(m.runningBalance)}</td>
        <td className={`${tdClass} text-right`}>
          {!m.voided && !voiding ? (
            <Button variant="ghost" onClick={() => setVoiding(true)}>
              Anular
            </Button>
          ) : null}
        </td>
      </tr>
      {voiding ? (
        <tr>
          <td className={tdClass} colSpan={7}>
            <div className="flex flex-wrap items-end gap-2">
              <Field label={`Motivo para anular este ${m.kind === "charge" ? "cargo" : "cobro"}`}>
                <Input
                  autoFocus
                  className="w-96"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ej.: cargado dos veces"
                />
              </Field>
              <Button
                variant="danger"
                disabled={!reason.trim() || voidIt.isPending}
                onClick={() => voidIt.mutate()}
              >
                Anular
              </Button>
              <Button variant="secondary" onClick={() => setVoiding(false)}>
                Cancelar
              </Button>
              <span className="text-xs text-brand-muted">
                Queda en la lista como anulado y deja de contar en el saldo. No se puede deshacer.
              </span>
            </div>
            <ErrorBox error={voidIt.error} />
          </td>
        </tr>
      ) : null}
    </>
  );
}

function useOnceKey() {
  const [key, setKey] = useState(() => crypto.randomUUID());
  return [key, () => setKey(crypto.randomUUID())] as const;
}

function ChargeForm({ companyId, onDone }: { companyId: string; onDone: () => void }) {
  const client = useQueryClient();
  const [key, renewKey] = useOnceKey();
  const concepts = useQuery({
    queryKey: ["receivables", "concepts"],
    queryFn: () => api.get<Concept[]>("/api/receivables/concepts"),
  });
  const [conceptName, setConceptName] = useState("");
  const [description, setDescription] = useState("");
  const [chargeDate, setChargeDate] = useState(today());
  const [dueDate, setDueDate] = useState("");
  const [amount, setAmount] = useState("");
  const [amountError, setAmountError] = useState<string>();
  const save = useMutation({
    mutationFn: (body: object) =>
      api.postOnce(`/api/receivables/companies/${companyId}/charges`, body, key),
    onSuccess: async () => {
      renewKey();
      await client.invalidateQueries({ queryKey: ["receivables"] });
      onDone();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const parsed = parseUserAmount(amount);
    if (!parsed || Number(parsed) <= 0) {
      setAmountError("Escribí un importe mayor que cero, por ejemplo 150.000,00.");
      return;
    }
    setAmountError(undefined);
    save.mutate({
      conceptName,
      description: description || undefined,
      chargeDate,
      dueDate: dueDate || undefined,
      amount: parsed,
    });
  };
  return (
    <Card title="Nuevo cargo" className="mb-4">
      <form onSubmit={submit} className="grid grid-cols-1 gap-3 md:grid-cols-6">
        <div className="md:col-span-2">
          <Field label="Concepto" hint="Elegí uno de la lista o escribí uno nuevo.">
            <Input
              list="receivable-concepts"
              value={conceptName}
              onChange={(e) => setConceptName(e.target.value)}
              required
            />
            <datalist id="receivable-concepts">
              {concepts.data
                ?.filter((c) => !c.archived)
                .map((c) => (
                  <option key={c.name} value={c.name} />
                ))}
            </datalist>
          </Field>
        </div>
        <div className="md:col-span-2">
          <Field label="Detalle (opcional)">
            <Input value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
        </div>
        <Field label="Importe" error={amountError}>
          <Input
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            required
          />
        </Field>
        <Field label="Fecha">
          <Input
            type="date"
            value={chargeDate}
            onChange={(e) => setChargeDate(e.target.value)}
            required
          />
        </Field>
        <Field label="Vence (opcional)">
          <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </Field>
        <div className="flex items-end gap-2 md:col-span-5">
          <Button type="submit" disabled={save.isPending}>
            Guardar cargo
          </Button>
          <Button variant="secondary" onClick={onDone}>
            Cancelar
          </Button>
        </div>
      </form>
      <ErrorBox error={save.error} />
    </Card>
  );
}

function PaymentForm({ companyId, onDone }: { companyId: string; onDone: () => void }) {
  const client = useQueryClient();
  const [key, renewKey] = useOnceKey();
  const [paymentDate, setPaymentDate] = useState(today());
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState<PaymentMethod>("transferencia");
  const [note, setNote] = useState("");
  const [amountError, setAmountError] = useState<string>();
  const save = useMutation({
    mutationFn: (body: object) =>
      api.postOnce(`/api/receivables/companies/${companyId}/payments`, body, key),
    onSuccess: async () => {
      renewKey();
      await client.invalidateQueries({ queryKey: ["receivables"] });
      onDone();
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const parsed = parseUserAmount(amount);
    if (!parsed || Number(parsed) <= 0) {
      setAmountError("Escribí un importe mayor que cero, por ejemplo 150.000,00.");
      return;
    }
    setAmountError(undefined);
    save.mutate({ paymentDate, amount: parsed, method, note: note || undefined });
  };
  return (
    <Card title="Nuevo cobro" className="mb-4">
      <form onSubmit={submit} className="grid grid-cols-1 gap-3 md:grid-cols-5">
        <Field label="Fecha">
          <Input
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            required
          />
        </Field>
        <Field label="Importe" error={amountError}>
          <Input
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            required
          />
        </Field>
        <Field label="Medio">
          <Select value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
            {PAYMENT_METHODS.map((m) => (
              <option key={m} value={m}>
                {PAYMENT_METHOD_LABELS[m]}
              </option>
            ))}
          </Select>
        </Field>
        <div className="md:col-span-2">
          <Field label="Nota (opcional)">
            <Input value={note} onChange={(e) => setNote(e.target.value)} />
          </Field>
        </div>
        <div className="flex items-end gap-2 md:col-span-5">
          <Button type="submit" disabled={save.isPending}>
            Guardar cobro
          </Button>
          <Button variant="secondary" onClick={onDone}>
            Cancelar
          </Button>
        </div>
      </form>
      <ErrorBox error={save.error} />
    </Card>
  );
}
